import React, { useEffect, useState } from "react";
import { X, UserPlus, UserCheck, MessageCircle, Loader2, Mic, Ban, ShieldCheck } from "lucide-react";
import { fetchAuthorPosts, fetchProfile } from "../lib/dmApi";
import { fetchBlockStatus, toggleFollow as apiToggleFollow, toggleUserBlock } from "../lib/socialApi";
import { loadIdentity } from "../lib/identity";

/** Popup with an author's public info + last posts + Follow + Start DM buttons. */
export function AuthorMiniProfile({ identityId, onClose, onStartDm }) {
  const me = loadIdentity();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [blockedByMe, setBlockedByMe] = useState(false);
  const [interactionBlocked, setInteractionBlocked] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetchProfile(identityId),
      fetchAuthorPosts(identityId),
      me && me.identityId !== identityId
        ? fetchBlockStatus(identityId).catch((statusError) => {
          // The profile and its posts are public. On Android the HttpOnly
          // session may still be restoring when a result from the global
          // search is opened; a temporary 401 in this supplementary check
          // must not replace the whole public mini-profile with an expiry
          // warning. Protected block/follow mutations remain authenticated.
          if (statusError?.code === "PUBLIC_SESSION_EXPIRED"
            || statusError?.response?.status === 401) {
            return { blockedByMe: false, interactionBlocked: false };
          }
          throw statusError;
        })
        : Promise.resolve({ blockedByMe: false, interactionBlocked: false }),
    ])
      .then(([p, items, blockStatus]) => {
        if (cancelled) return;
        setProfile(p);
        setBlockedByMe(!!blockStatus?.blockedByMe);
        setInteractionBlocked(!!blockStatus?.interactionBlocked);
        setPosts(blockStatus?.interactionBlocked ? [] : items);
        if (!blockStatus?.interactionBlocked && items?.[0]?.followedByMe != null) {
          setFollowing(!!items[0].followedByMe);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || "Errore caricamento");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [identityId]);

  const toggleFollow = async () => {
    if (busy || !me || me.identityId === identityId) return;
    setBusy(true);
    try {
      const action = following ? "unfollow" : "follow";
      // Use the shared helper — it injects `followerId` from the loaded
      // identity so the backend contract (follows.controller assertShape)
      // is honoured. Calling signedPost directly here bypassed that and
      // caused the mini-profile follow to fail with 400 (iter22 P0).
      await apiToggleFollow(identityId, action);
      const nowFollowing = !following;
      setFollowing(nowFollowing);
      const displayName = profile?.displayName || "l'utente";
      setToast(nowFollowing ? `Ora segui ${displayName}` : `Non segui più ${displayName}`);
      setTimeout(() => setToast(null), 2400);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Errore follow");
    } finally { setBusy(false); }
  };

  const toggleBlock = async () => {
    if (busy || !me || me.identityId === identityId) return;
    if (!blockedByMe) {
      const accepted = window.confirm(
        "Bloccare questo utente? Non vedrai più i suoi contenuti e non potrete scambiarvi messaggi privati.",
      );
      if (!accepted) return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await toggleUserBlock(identityId, blockedByMe ? "unblock" : "block");
      const nextBlocked = !!result?.blocked;
      setBlockedByMe(nextBlocked);
      setInteractionBlocked(nextBlocked);
      if (nextBlocked) {
        setFollowing(false);
        setPosts([]);
        setToast("Utente bloccato");
      } else {
        setToast("Utente sbloccato");
        const [items, status] = await Promise.all([
          fetchAuthorPosts(identityId),
          fetchBlockStatus(identityId),
        ]);
        setInteractionBlocked(!!status?.interactionBlocked);
        setPosts(status?.interactionBlocked ? [] : items);
      }
      setTimeout(() => setToast(null), 2400);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Impossibile aggiornare il blocco");
    } finally {
      setBusy(false);
    }
  };

  const isSelf = !!(me && me.identityId === identityId);

  return (
    <div
      className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="mini-profile-modal"
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-5 relative max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 rounded-full hover:bg-cream flex items-center justify-center"
          data-testid="mini-profile-close"
          aria-label="Chiudi"
        >
          <X size={16} />
        </button>

        {loading && (
          <div className="flex items-center gap-2 text-xs text-muted py-6 justify-center">
            <Loader2 size={13} className="animate-spin" /> Carico profilo…
          </div>
        )}

        {!loading && error && (
          <div className="text-sm text-primary py-6" data-testid="mini-profile-error">{error}</div>
        )}

        {!loading && profile && (
          <>
            {toast && (
              <div
                className="absolute top-3 left-1/2 -translate-x-1/2 bg-ink text-white text-xs px-4 py-2 rounded-full shadow-lg animate-in"
                data-testid="mini-profile-toast"
                role="status"
              >
                {toast}
              </div>
            )}
            <div className="flex items-start gap-3 pr-8">
              {profile.avatar ? (
                <img src={profile.avatar} alt="" className="w-14 h-14 rounded-full object-cover border border-edge shrink-0" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-ink text-white flex items-center justify-center text-lg font-semibold shrink-0">
                  {(profile.displayName || "?").slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 pt-1">
                <div className="font-display font-semibold text-xl text-ink leading-tight truncate" data-testid="mini-profile-name">
                  {profile.displayName || "Utente"}
                </div>
                <div className="text-[11px] font-mono text-muted truncate">{profile.identityId}</div>
              </div>
            </div>

            {!isSelf && (
              <div className="mt-4 flex items-center gap-2">
                <button
                  onClick={toggleFollow}
                  disabled={busy || interactionBlocked}
                  className={`flex-1 py-2 rounded-full text-xs font-semibold flex items-center justify-center gap-1.5 transition disabled:opacity-50 ${
                    following ? "bg-cream border border-edge text-ink hover:border-ink" : "bg-ink text-white hover:bg-black"
                  }`}
                  data-testid="mini-profile-follow"
                >
                  {following ? <UserCheck size={14} /> : <UserPlus size={14} />}
                  {following ? "Segui già" : "Segui"}
                </button>
                <button
                  onClick={() => onStartDm(profile.identityId)}
                  disabled={interactionBlocked || !profile.x25519PublicKeyHex}
                  title={profile.x25519PublicKeyHex ? "Manda un messaggio cifrato" : "Utente senza chiavi DM"}
                  className="flex-1 py-2 rounded-full text-xs font-semibold flex items-center justify-center gap-1.5 bg-primary text-white hover:bg-primary/90 disabled:opacity-40"
                  data-testid="mini-profile-message"
                >
                  <MessageCircle size={14} /> Manda un messaggio
                </button>
              </div>
            )}

            {!isSelf && (
              <button
                type="button"
                onClick={toggleBlock}
                disabled={busy}
                className={`mt-2 w-full py-2 rounded-full border text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50 ${
                  blockedByMe
                    ? "border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                    : "border-primary/40 text-primary hover:bg-primary/5"
                }`}
                data-testid="mini-profile-block"
              >
                {blockedByMe ? <ShieldCheck size={14} /> : <Ban size={14} />}
                {blockedByMe ? "Sblocca utente" : "Blocca utente"}
              </button>
            )}

            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-widest text-muted mb-2">Ultimi post</div>
              {posts.length === 0 ? (
                <div className="text-xs text-muted py-4 text-center">Nessun post pubblico.</div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {posts.slice(0, 10).map((p) => (
                    <div
                      key={p.id}
                      className="bg-cream rounded-2xl px-3 py-2 flex items-center gap-2 border border-transparent hover:border-edge"
                      data-testid={`mini-profile-post-${p.id}`}
                    >
                      <Mic size={14} className="text-muted shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-semibold text-ink truncate">
                          {p.caption?.slice(0, 80) || "Vocale"}
                        </div>
                        <div className="text-[10px] text-muted">
                          {p.topicSlug} · {p.duration || Math.round((p.durationSeconds || 0) / 60)}m
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
